import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiError, POUND } from "../lib/api";
import { toast } from "sonner";
import { MaterialThumb } from "../lib/materialThumbs";

const empty = {
  manufacturer: "", supplier: "", category: "", name: "", sku: "", unit: "unit",
  price: 0, coverage_per_unit: 1, waste_pct: 5, description: "",
  // Optional manufacturer technical data (BS 5534 / mfr)
  tech_family: "", min_pitch_deg: "", max_pitch_deg: "",
  length_mm: "", width_mm: "", weight_kg_per_m2: "", tiles_per_m2: "",
  standard_ref: "",
};

const TECH_FAMILIES = [
  { id: "", label: "— Not a covering —" },
  { id: "concrete-interlocking", label: "Concrete interlocking" },
  { id: "concrete-low-pitch", label: "Concrete (low pitch)" },
  { id: "clay-plain", label: "Clay plain tile" },
  { id: "clay-pantile", label: "Clay pantile" },
  { id: "natural-slate", label: "Natural slate" },
  { id: "fc-slate", label: "Fibre cement slate" },
  { id: "metal-ss", label: "Metal standing seam" },
  { id: "metal-corrugated", label: "Metal corrugated" },
  { id: "membrane-epdm", label: "EPDM membrane" },
  { id: "membrane-grp", label: "GRP membrane" },
  { id: "membrane-felt", label: "Torch-on felt" },
  { id: "underlay-breathable", label: "Breather underlay" },
  { id: "batten", label: "Roof batten" },
];

function Dialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...empty, ...editing } : empty);
  }, [open, editing]);

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, [k]: e.target.value === "" ? "" : parseFloat(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Normalise: blank string tech fields → null (Pydantic Optional[float])
      const tech_num = ["min_pitch_deg", "max_pitch_deg", "length_mm", "width_mm", "weight_kg_per_m2", "tiles_per_m2"];
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        coverage_per_unit: Number(form.coverage_per_unit) || 1,
        waste_pct: Number(form.waste_pct) || 0,
      };
      tech_num.forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
      });
      ["tech_family", "standard_ref", "supplier", "sku", "description"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (editing?.id) {
        const { data } = await api.put(`/materials/${editing.id}`, payload);
        toast.success("Material updated");
        onSaved(data);
      } else {
        const { data } = await api.post("/materials", payload);
        toast.success("Material added");
        onSaved(data);
      }
      onClose();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="card-elev w-full max-w-3xl p-6 my-8" data-testid="material-dialog">
        <div className="overline">{editing ? "Edit" : "New"} material</div>
        <h3 className="font-display text-2xl font-bold mt-1">{editing ? editing.name : "Add Material"}</h3>

        <div className="overline mt-5 text-[var(--rqp-blue)]">Identification &amp; commercials</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
          <label className="block sm:col-span-2"><span className="overline">Manufacturer</span><input data-testid="material-manufacturer" required className="rqp mt-1.5" value={form.manufacturer} onChange={set("manufacturer")} /></label>
          <label className="block"><span className="overline">Supplier / Merchant</span><input className="rqp mt-1.5" value={form.supplier || ""} onChange={set("supplier")} placeholder="SIG Roofing, Burton Roofing…" /></label>
          <label className="block sm:col-span-2"><span className="overline">Name</span><input data-testid="material-name" required className="rqp mt-1.5" value={form.name} onChange={set("name")} /></label>
          <label className="block"><span className="overline">SKU / Code</span><input className="rqp mt-1.5" value={form.sku || ""} onChange={set("sku")} /></label>
          <label className="block"><span className="overline">Category</span><input data-testid="material-category" required className="rqp mt-1.5" value={form.category} onChange={set("category")} placeholder="Concrete Tiles" /></label>
          <label className="block"><span className="overline">Unit</span><input className="rqp mt-1.5" value={form.unit} onChange={set("unit")} placeholder="tile, m, m²" /></label>
          <label className="block"><span className="overline">Price (£)</span><input data-testid="material-price" type="number" step="0.01" className="rqp mt-1.5" value={form.price} onChange={setNum("price")} /></label>
          <label className="block"><span className="overline">Coverage / unit</span><input type="number" step="0.001" className="rqp mt-1.5" value={form.coverage_per_unit} onChange={setNum("coverage_per_unit")} /></label>
          <label className="block"><span className="overline">Waste %</span><input type="number" step="0.5" className="rqp mt-1.5" value={form.waste_pct} onChange={setNum("waste_pct")} /></label>
        </div>

        <div className="overline mt-5 text-[var(--rqp-blue)]">BS 5534 / Manufacturer technical data (optional)</div>
        <p className="text-xs text-slate-500 mb-2">Used by the compliance engine to compute batten gauge, fixing schedule, ventilation and structural weight.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="overline">Tech family</span>
            <select className="rqp mt-1.5" value={form.tech_family || ""} onChange={(e) => setForm({ ...form, tech_family: e.target.value })} data-testid="material-tech-family">
              {TECH_FAMILIES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label className="block"><span className="overline">Min pitch (°)</span><input type="number" step="0.5" className="rqp mt-1.5" value={form.min_pitch_deg ?? ""} onChange={setNum("min_pitch_deg")} data-testid="material-min-pitch" /></label>
          <label className="block"><span className="overline">Max pitch (°)</span><input type="number" step="0.5" className="rqp mt-1.5" value={form.max_pitch_deg ?? ""} onChange={setNum("max_pitch_deg")} /></label>
          <label className="block"><span className="overline">Cover length (mm)</span><input type="number" step="1" className="rqp mt-1.5" value={form.length_mm ?? ""} onChange={setNum("length_mm")} data-testid="material-length-mm" /></label>
          <label className="block"><span className="overline">Cover width (mm)</span><input type="number" step="1" className="rqp mt-1.5" value={form.width_mm ?? ""} onChange={setNum("width_mm")} /></label>
          <label className="block"><span className="overline">Weight (kg/m²)</span><input type="number" step="0.1" className="rqp mt-1.5" value={form.weight_kg_per_m2 ?? ""} onChange={setNum("weight_kg_per_m2")} data-testid="material-weight" /></label>
          <label className="block"><span className="overline">Tiles / m²</span><input type="number" step="0.1" className="rqp mt-1.5" value={form.tiles_per_m2 ?? ""} onChange={setNum("tiles_per_m2")} data-testid="material-tiles-per-m2" /></label>
          <label className="block sm:col-span-2"><span className="overline">Standard reference</span><input className="rqp mt-1.5" value={form.standard_ref || ""} onChange={set("standard_ref")} placeholder="BS 5534 NA.4 / Mfr TG ref" /></label>
        </div>

        <label className="block mt-4"><span className="overline">Description</span><textarea rows={2} className="rqp mt-1.5" value={form.description || ""} onChange={set("description")} /></label>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving} data-testid="material-save">{saving ? "Saving…" : (editing ? "Update" : "Add")}</button>
        </div>
      </form>
    </div>
  );
}

export default function Materials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [manuFilter, setManuFilter] = useState("All");
  const [catFilter, setCatFilter] = useState("All");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/materials");
        if (alive) setItems(data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const manufacturers = useMemo(() => ["All", ...Array.from(new Set(items.map((x) => x.manufacturer)))], [items]);
  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((x) => x.category)))], [items]);

  const filtered = items.filter((m) => {
    if (manuFilter !== "All" && m.manufacturer !== manuFilter) return false;
    if (catFilter !== "All" && m.category !== catFilter) return false;
    if (q) {
      const t = q.toLowerCase();
      if (![m.name, m.sku, m.manufacturer, m.category].some((x) => (x || "").toLowerCase().includes(t))) return false;
    }
    return true;
  });

  const remove = async (m) => {
    if (m.is_default) { toast.error("Default materials cannot be deleted"); return; }
    if (!window.confirm("Delete this material?")) return;
    try {
      await api.delete(`/materials/${m.id}`);
      toast.success("Deleted");
      setItems((x) => x.filter((i) => i.id !== m.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <span className="hero-rule" />
          <div className="overline">Catalogue</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">Materials Library</h1>
          <p className="text-sm text-slate-500 mt-2">Pre-seeded with Marley, Redland, BMI, Klober, Firestone, Cure It and more.</p>
        </div>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary flex items-center gap-2" data-testid="materials-add">
          <Plus size={16} weight="bold" /> Add Material
        </button>
      </div>

      <div className="card-elev p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="materials-search" className="rqp pl-8" placeholder="Search by name, SKU…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className="rqp w-auto" value={manuFilter} onChange={(e) => setManuFilter(e.target.value)} data-testid="materials-manufacturer-filter">
          {manufacturers.map((m) => <option key={m}>{m}</option>)}
        </select>
        <select className="rqp w-auto" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="materials-category-filter">
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="card-elev overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="overline text-left p-3 w-14"></th>
              <th className="overline text-left p-3">Manufacturer</th>
              <th className="overline text-left p-3">Category</th>
              <th className="overline text-left p-3">Name</th>
              <th className="overline text-left p-3">SKU</th>
              <th className="overline text-right p-3">Price</th>
              <th className="overline text-left p-3">Unit</th>
              <th className="overline text-right p-3">Waste %</th>
              <th className="overline text-right p-3"></th>
            </tr>
          </thead>
          <tbody className="font-mono-data">
            {loading ? (
              <tr><td colSpan={9} className="p-6 text-slate-500 text-center font-sans" data-testid="materials-loading">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="p-6 text-slate-500 text-center font-sans">No materials match your filters.</td></tr>
            ) : filtered.map((m) => (
              <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`material-row-${m.id}`}>
                <td className="p-2"><MaterialThumb category={m.category} name={m.name} /></td>
                <td className="p-3 font-sans">{m.manufacturer}</td>
                <td className="p-3 font-sans text-slate-600 text-xs">{m.category}</td>
                <td className="p-3 font-sans">{m.name}</td>
                <td className="p-3 text-xs text-slate-500">{m.sku || "—"}</td>
                <td className="p-3 text-right font-semibold">{POUND(m.price)}</td>
                <td className="p-3 text-xs text-slate-600">/ {m.unit}</td>
                <td className="p-3 text-right">{m.waste_pct}%</td>
                <td className="p-3 text-right">
                  {!m.is_default && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setEditing(m); setOpen(true); }} className="btn-secondary !px-2 !py-1" data-testid={`material-edit-${m.id}`}><Pencil size={12} weight="bold" /></button>
                      <button onClick={() => remove(m)} className="btn-secondary !px-2 !py-1 text-red-600" data-testid={`material-delete-${m.id}`}><Trash size={12} weight="bold" /></button>
                    </div>
                  )}
                  {m.is_default && <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Default</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} editing={editing} onSaved={(saved) => {
        setItems((prev) => {
          const idx = prev.findIndex((p) => p.id === saved.id);
          if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
          return [saved, ...prev];
        });
      }} />
    </div>
  );
}
